import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { FeatureDisabledState } from "../../components/trainer/FeatureDisabledState";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useClientDetail } from "../../hooks/useClientDetail";
import { useTenant } from "../../hooks/useTenant";
import { useWorkspaceFeatures } from "../../hooks/useWorkspaceFeatures";
import { formatDate, formatDateTime, statusTone } from "../../lib/format";
import { clientService } from "../../services/clientService";
import { mealPlanService } from "../../services/mealPlanService";
import { workoutPlanService } from "../../services/workoutPlanService";
import type { MealPlan, WorkoutPlan } from "../../types/coaching";
import type { ClientOnboardingResponse } from "../../types/trainer";

const FUTURE_SECTIONS = ["Workout plans", "Check-ins", "Progress", "Nutrition", "Messages"];

export function TrainerClientDetailPage() {
  const { clientId } = useParams();
  const { currentTenant } = useTenant();
  const { data: features } = useWorkspaceFeatures(currentTenant?.id);
  const { data: client, loading, error } = useClientDetail(currentTenant?.id, clientId);
  const [onboarding, setOnboarding] = useState<ClientOnboardingResponse | null>(null);
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [selectedWorkoutPlanId, setSelectedWorkoutPlanId] = useState("");
  const [selectedMealPlanId, setSelectedMealPlanId] = useState("");
  const [workoutStartsOn, setWorkoutStartsOn] = useState("");
  const [workoutEndsOn, setWorkoutEndsOn] = useState("");
  const [mealStartsOn, setMealStartsOn] = useState("");
  const [mealEndsOn, setMealEndsOn] = useState("");
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignmentSuccess, setAssignmentSuccess] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<"workout" | "meal" | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadOnboarding() {
      if (!client?.id) return;

      try {
        const response = await clientService.getOnboardingResponse(client.id);
        if (mounted) setOnboarding(response);
      } catch (caughtError) {
        console.error("Unable to load client onboarding summary", caughtError);
      }
    }

    void loadOnboarding();

    return () => {
      mounted = false;
    };
  }, [client?.id]);

  async function loadPlans() {
    if (!client?.id || !currentTenant?.id) return;

    try {
      const [workouts, meals] = await Promise.all([
        workoutPlanService.listWorkoutPlansForTrainer(currentTenant.id),
        mealPlanService.listMealPlansForTrainer(currentTenant.id),
      ]);
      setWorkoutPlans(workouts);
      setMealPlans(meals);
    } catch (caughtError) {
      console.error("Unable to load coaching plans", caughtError);
      setAssignmentError("Unable to load coaching plans.");
    }
  }

  useEffect(() => {
    void loadPlans();
  }, [client?.id, currentTenant?.id]);

  const assignedWorkoutPlans = workoutPlans.filter(
    (plan) => plan.client_id === client?.id && plan.status === "active",
  );
  const assignedMealPlans = mealPlans.filter(
    (plan) => plan.client_id === client?.id && plan.status === "active",
  );
  const assignableWorkoutPlans = workoutPlans.filter(
    (plan) => plan.status !== "archived" && plan.client_id !== client?.id,
  );
  const assignableMealPlans = mealPlans.filter(
    (plan) => plan.status !== "archived" && plan.client_id !== client?.id,
  );

  async function handleAssignWorkout() {
    if (!client?.id || !selectedWorkoutPlanId) return;
    setAssigning("workout");
    setAssignmentError(null);
    setAssignmentSuccess(null);
    try {
      await workoutPlanService.assignWorkoutPlanToClient(selectedWorkoutPlanId, client.id, {
        startsOn: workoutStartsOn || null,
        endsOn: workoutEndsOn || null,
      });
      setSelectedWorkoutPlanId("");
      setWorkoutStartsOn("");
      setWorkoutEndsOn("");
      setAssignmentSuccess("Workout plan assigned.");
      await loadPlans();
    } catch (caughtError) {
      console.error("Unable to assign workout plan", caughtError);
      setAssignmentError("Unable to assign workout plan.");
    } finally {
      setAssigning(null);
    }
  }

  async function handleAssignMeal() {
    if (!client?.id || !selectedMealPlanId) return;
    setAssigning("meal");
    setAssignmentError(null);
    setAssignmentSuccess(null);
    try {
      await mealPlanService.assignMealPlanToClient(selectedMealPlanId, client.id, {
        startsOn: mealStartsOn || null,
        endsOn: mealEndsOn || null,
      });
      setSelectedMealPlanId("");
      setMealStartsOn("");
      setMealEndsOn("");
      setAssignmentSuccess("Meal plan assigned.");
      await loadPlans();
    } catch (caughtError) {
      console.error("Unable to assign meal plan", caughtError);
      setAssignmentError("Unable to assign meal plan.");
    } finally {
      setAssigning(null);
    }
  }

  if (loading) return <FullPageLoader label="Loading client" />;
  if (error) return <EmptyState title="Unable to load client.">{error}</EmptyState>;
  if (features && !features.client_profiles_enabled) {
    return <FeatureDisabledState title="Client profiles are not enabled." />;
  }
  if (!client) return <EmptyState title="Client not found." />;

  return (
    <div>
      <AdminPageHeader title={client.full_name} description="Client profile foundation for this workspace." />
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <AdminPanel title="Client details">
            <dl className="grid gap-4 md:grid-cols-2">
              <div>
                <dt className="text-sm text-graphite/60">Email</dt>
                <dd className="mt-1 font-medium text-ink">{client.email}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Phone</dt>
                <dd className="mt-1 font-medium text-ink">{client.phone ?? "Not set"}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Status</dt>
                <dd className="mt-1"><Badge tone={statusTone(client.status)}>{client.status}</Badge></dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Assigned trainer</dt>
                <dd className="mt-1 font-medium text-ink">{client.assigned_trainer?.full_name || client.assigned_trainer?.email || "Unassigned"}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Goal</dt>
                <dd className="mt-1 font-medium text-ink">{client.goal ?? "Not set"}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Created</dt>
                <dd className="mt-1 font-medium text-ink">{formatDate(client.created_at)}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Date of birth</dt>
                <dd className="mt-1 font-medium text-ink">{client.date_of_birth ? formatDate(client.date_of_birth) : "Not set"}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Source</dt>
                <dd className="mt-1 font-medium text-ink">{client.source ?? "Not set"}</dd>
              </div>
              <div>
                <dt className="text-sm text-graphite/60">Emergency contact</dt>
                <dd className="mt-1 font-medium text-ink">
                  {[client.emergency_contact_name, client.emergency_contact_phone].filter(Boolean).join(" - ") || "Not set"}
                </dd>
              </div>
            </dl>
          </AdminPanel>
          <AdminPanel title="Notes">
            <p className="whitespace-pre-wrap text-sm leading-6 text-graphite/75">{client.notes || "No notes yet."}</p>
          </AdminPanel>
          <AdminPanel title="Assigned coaching content" description="Assign active workout and meal plans to this client.">
            {assignmentError ? <p className="mb-4 text-sm font-medium text-red-800">{assignmentError}</p> : null}
            {assignmentSuccess ? <p className="mb-4 text-sm font-medium text-pine">{assignmentSuccess}</p> : null}
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="space-y-4 rounded-md border border-line bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-ink">Assigned workout plans</h3>
                  <Link className="link text-sm" to="/trainer/workouts">Create one first</Link>
                </div>
                {assignedWorkoutPlans.length ? (
                  <div className="space-y-2">
                    {assignedWorkoutPlans.map((plan) => (
                      <div className="rounded-md bg-field p-3 text-sm" key={plan.id}>
                        <p className="font-medium text-ink">{plan.title}</p>
                        <p className="text-graphite/65">Assigned {formatDate(plan.assigned_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-graphite/65">No active workout plans assigned.</p>
                )}
                <label className="block text-sm font-medium text-ink">
                  Assign workout plan
                  <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={selectedWorkoutPlanId} onChange={(event) => setSelectedWorkoutPlanId(event.target.value)}>
                    <option value="">Select a plan</option>
                    {assignableWorkoutPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.title} {plan.is_template ? "(Template)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                {!assignableWorkoutPlans.length ? <p className="text-sm text-graphite/65">No workout plans yet. Create one first.</p> : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Start date" type="date" value={workoutStartsOn} onChange={(event) => setWorkoutStartsOn(event.target.value)} />
                  <Input label="End date" type="date" value={workoutEndsOn} onChange={(event) => setWorkoutEndsOn(event.target.value)} />
                </div>
                <Button disabled={!selectedWorkoutPlanId} isLoading={assigning === "workout"} onClick={() => void handleAssignWorkout()}>
                  Assign workout plan
                </Button>
              </div>

              <div className="space-y-4 rounded-md border border-line bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-ink">Assigned meal plans</h3>
                  <Link className="link text-sm" to="/trainer/meals">Create one first</Link>
                </div>
                {assignedMealPlans.length ? (
                  <div className="space-y-2">
                    {assignedMealPlans.map((plan) => (
                      <div className="rounded-md bg-field p-3 text-sm" key={plan.id}>
                        <p className="font-medium text-ink">{plan.title}</p>
                        <p className="text-graphite/65">Assigned {formatDate(plan.assigned_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-graphite/65">No active meal plans assigned.</p>
                )}
                <label className="block text-sm font-medium text-ink">
                  Assign meal plan
                  <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={selectedMealPlanId} onChange={(event) => setSelectedMealPlanId(event.target.value)}>
                    <option value="">Select a plan</option>
                    {assignableMealPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.title} {plan.is_template ? "(Template)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                {!assignableMealPlans.length ? <p className="text-sm text-graphite/65">No meal plans yet. Create one first.</p> : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Start date" type="date" value={mealStartsOn} onChange={(event) => setMealStartsOn(event.target.value)} />
                  <Input label="End date" type="date" value={mealEndsOn} onChange={(event) => setMealEndsOn(event.target.value)} />
                </div>
                <Button disabled={!selectedMealPlanId} isLoading={assigning === "meal"} onClick={() => void handleAssignMeal()}>
                  Assign meal plan
                </Button>
              </div>
            </div>
          </AdminPanel>
          <AdminPanel title="Onboarding summary">
            {onboarding ? (
              <dl className="grid gap-4 md:grid-cols-2">
                <div>
                  <dt className="text-sm text-graphite/60">Submitted</dt>
                  <dd className="mt-1 font-medium text-ink">{formatDateTime(onboarding.submitted_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Goal</dt>
                  <dd className="mt-1 font-medium text-ink">{onboarding.primary_goal || "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Training level</dt>
                  <dd className="mt-1 font-medium text-ink">{onboarding.current_training_level ?? "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Dietary requirements</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.dietary_requirements ?? "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Injuries or limitations</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.injuries_limitations ?? "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Medication</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.medication_disclosure ?? "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">Supplements</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.supplement_disclosure ?? "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-graphite/60">PED disclosure</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.ped_disclosure ?? "Not set"}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm text-graphite/60">Lifestyle notes</dt>
                  <dd className="mt-1 whitespace-pre-wrap font-medium text-ink">{onboarding.lifestyle_notes ?? "Not set"}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm leading-6 text-graphite/75">No onboarding response submitted yet.</p>
            )}
          </AdminPanel>
        </div>
        <div className="space-y-6">
          <AdminPanel title="Future modules">
            <div className="space-y-2">
              {FUTURE_SECTIONS.map((section) => (
                <div className="rounded-md border border-line bg-field px-3 py-2 text-sm text-graphite/70" key={section}>
                  {section} - not built yet
                </div>
              ))}
            </div>
          </AdminPanel>
          <AdminPanel title="Navigation">
            <Link className="link text-sm" to="/trainer/clients">Back to clients</Link>
          </AdminPanel>
        </div>
      </div>
    </div>
  );
}
